import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Card, CardBody, Button, Input, Alert } from '@components/ui';
import { Logo } from '@components/common/Logo';
import { authService } from '../services/authService';

const resetPasswordSchema = z
  .object({
    code: z.string().length(4, 'El código debe tener 4 dígitos').regex(/^\d{4}$/, 'El código debe contener solo números'),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // Ya no se pide el userId con getUserByEmail: el backend lo guarda de su
  // lado al enviar el correo y solo lo entrega dentro del ticket que emite al
  // verificar el codigo. Verificar es ahora el paso que AUTORIZA el reset —
  // sin el ticket, /auth/reset-password rechaza — asi que este formulario ya
  // no necesita (ni puede) resolver a que usuario pertenece el correo.
  const mutation = useMutation({
    mutationFn: async (data: ResetPasswordFormData) => {
      const { resetTicket } = await authService.verifyResetCode(data.code, email);
      return authService.resetPassword(resetTicket, data.password, data.confirmPassword);
    },
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    mutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-100 dark:bg-secondary-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo height={80} />
          </div>
          <p className="text-secondary-600 dark:text-secondary-400">
            Portal de Facturación Electrónica
          </p>
        </div>

        <Card>
          <CardBody className="p-8">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
              Crear Nueva Contraseña
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-6">
              Ingresa tu nueva contraseña. Asegúrate de que sea segura.
            </p>

            {!email ? (
              <div className="space-y-6">
                <Alert variant="danger" title="Enlace Inválido" data-testid="auth-reset-password-page-invalid-link-alert">
                  El enlace de recuperación de contraseña es inválido. Por favor, solicita uno
                  nuevo.
                </Alert>
                <Link to="/forgot-password">
                  <Button variant="primary" className="w-full" data-testid="auth-reset-password-page-request-new-link-button">
                    Solicitar Nuevo Enlace
                  </Button>
                </Link>
              </div>
            ) : success ? (
              <div className="space-y-6">
                <Alert variant="success" title="Contraseña Actualizada" data-testid="auth-reset-password-page-success-alert">
                  Tu contraseña ha sido actualizada exitosamente. Redirigiendo al login...
                </Alert>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="auth-reset-password-page-form">
                {mutation.error && (
                  <Alert variant="danger" title="Error" data-testid="auth-reset-password-page-error-alert">
                    El código es incorrecto o ha expirado. Por favor, verifica el código enviado a
                    tu correo o solicita uno nuevo.
                  </Alert>
                )}

                <Alert variant="info" title="Código de Verificación" data-testid="auth-reset-password-page-code-sent-alert">
                  Hemos enviado un código de 4 dígitos a tu correo electrónico:{' '}
                  <strong>{email}</strong>
                </Alert>

                <Input
                  label="Código de Verificación"
                  type="text"
                  placeholder="1234"
                  maxLength={4}
                  error={errors.code?.message}
                  helperText="Ingresa el código de 4 dígitos enviado a tu correo"
                  data-testid="auth-reset-password-page-code-input"
                  {...register('code')}
                />

                <Input
                  label="Nueva Contraseña"
                  type="password"
                  placeholder="••••••••"
                  error={errors.password?.message}
                  helperText="Mínimo 8 caracteres"
                  data-testid="auth-reset-password-page-password-input"
                  {...register('password')}
                />

                <Input
                  label="Confirmar Contraseña"
                  type="password"
                  placeholder="••••••••"
                  error={errors.confirmPassword?.message}
                  data-testid="auth-reset-password-page-confirm-password-input"
                  {...register('confirmPassword')}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isLoading={mutation.isPending}
                  data-testid="auth-reset-password-page-submit-button"
                >
                  Actualizar Contraseña
                </Button>

                <Link to="/login">
                  <Button variant="ghost" size="lg" className="w-full" data-testid="auth-reset-password-page-back-to-login-button">
                    Volver al Login
                  </Button>
                </Link>
              </form>
            )}
          </CardBody>
        </Card>

        <p className="text-center text-sm text-secondary-600 dark:text-secondary-400 mt-6">
          © 2025 Facturación Electrónica. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
